import { useAuth } from "@/components/contexts/AuthContext";
import { CompanyContext } from "@/components/contexts/CompanyContext";
import { useGetCompanyById } from "@/hooks/data/v2/company-queries";
import { useGetJobSpecialtiesByFilter } from "@/hooks/data/v2/job-specialty-queries";
import { useGetLeadSourcesByFilter } from "@/hooks/data/v2/lead-source-queries";
import { useGetLeadTypesByFilter } from "@/hooks/data/v2/lead-type-queries";
import { useGetOfficesByFilter } from "@/hooks/data/v2/office-queries";
import { useGetTeamsByFilter } from "@/hooks/data/v2/team-queries";
import { useTagSearchQuery } from "@/hooks/data/v2/tag-queries";
import {
    useGetActiveUserOptions,
    useGetAllUserOptions,
    useGetUserOptionsByFilter,
    useGetUsersByFilter,
} from "@/hooks/data/v2/user-queries";
import type { ReactNode } from "react";

export function CompanyProvider({ children }: { children: ReactNode }) {
    const {
        authState: { user },
    } = useAuth();

    const getCompanyByIdQuery = useGetCompanyById(user.companyId ?? null);
    const getUsersByFilterQuery = useGetUsersByFilter({
        search: [{ key: "companyId", value: user.companyId ?? 0 }],
        getAll: true,
    });
    const getUsersWithLeadsOptionsQuery = useGetUserOptionsByFilter({});
    const getActiveUserOptionsQuery = useGetActiveUserOptions();
    const getAllUserOptionsQuery = useGetAllUserOptions();
    const getOfficesByFilterQuery = useGetOfficesByFilter({
        search: [{ key: "companyId", value: user.companyId ?? 0 }],
        getAll: true,
    });
    const getLeadSourcesByFilterQuery = useGetLeadSourcesByFilter({
        getAll: true,
        sort: { direction: "asc", key: "name" },
    });
    const getLeadTypesByFilterQuery = useGetLeadTypesByFilter({ getAll: true });
    const getJobSpecialtiesByFilterQuery = useGetJobSpecialtiesByFilter({ getAll: true });
    const getTeamsByFilterQuery = useGetTeamsByFilter({ getAll: true });
    const getTagsQuery = useTagSearchQuery({ getAll: true });

    return (
        <CompanyContext.Provider
            value={{
                company: getCompanyByIdQuery.data,
                users: getUsersByFilterQuery.data,
                usersOptionsWithLeads: getUsersWithLeadsOptionsQuery.data,
                activeUserOptions: getActiveUserOptionsQuery.data,
                allUserOptions: getAllUserOptionsQuery.data,
                allOffices: getOfficesByFilterQuery.data,
                allLeadSources: getLeadSourcesByFilterQuery.data,
                allLeadTypes: getLeadTypesByFilterQuery.data,
                allJobSpecialties: getJobSpecialtiesByFilterQuery.data,
                allTeams: getTeamsByFilterQuery.data,
                allTags: getTagsQuery.data,
            }}
        >
            {children}
        </CompanyContext.Provider>
    );
}
